export interface ProposalRequestAttachment {
  name?: string
  url?: string
  type?: string
  size?: number
  uploaded_at?: string
}

export interface ProposalRequestBlueprintLink {
  title?: string
  url?: string
  description?: string
}

export interface ProposalRequestIntakeNote {
  timestamp: string
  note: string
}

export interface ProposalRequestDisplayData {
  id?: string
  title?: string
  request_number?: string
  status?: string
  description?: string
  project_type?: string
  service_type?: string
  property_type?: string
  square_footage?: number | null
  estimated_budget?: number | null
  property_address?: string
  property_city?: string
  property_state?: string
  property_zip?: string
  preferred_start_date?: string
  preferred_completion_date?: string
  urgency_level?: string
  special_requirements?: string
  accessibility_needs?: string
  existing_contractor?: boolean
  contractor_details?: string
  internal_notes?: string
  review_notes?: string
  submitted_at?: string
  converted_at?: string
  attachments: ProposalRequestAttachment[]
  blueprint_links: ProposalRequestBlueprintLink[]
  intake_notes: ProposalRequestIntakeNote[]
}

export function parseJsonArray<T>(raw: unknown): T[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw as T[]
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? (parsed as T[]) : []
    } catch {
      return []
    }
  }
  return []
}

export function parseRequestIntakeNotes(raw: unknown): ProposalRequestIntakeNote[] {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw
      .filter((n) => n && typeof n === 'object' && 'note' in n)
      .map((n: any) => ({
        timestamp: n.timestamp || new Date().toISOString(),
        note: String(n.note || ''),
      }))
  }
  if (typeof raw === 'string') {
    try {
      return parseRequestIntakeNotes(JSON.parse(raw))
    } catch {
      return []
    }
  }
  return []
}

const REQUEST_SNAPSHOT_FIELDS = [
  'title',
  'description',
  'project_type',
  'service_type',
  'property_address',
  'property_city',
  'property_state',
  'property_zip',
  'property_type',
  'square_footage',
  'estimated_budget',
  'preferred_start_date',
  'preferred_completion_date',
  'urgency_level',
  'special_requirements',
  'accessibility_needs',
  'existing_contractor',
  'contractor_details',
  'contact_name',
  'contact_email',
  'contact_phone',
  'company_name',
  'lead_id',
  'customer_id',
  'property_id',
  'lead_project_id',
  'internal_notes',
  'review_notes',
  'request_number',
  'submitted_at',
  'status',
] as const

/** Snapshot request fields before same-record conversion overwrites proposal columns. */
export function buildProposalRequestSnapshot(request: Record<string, any>): Record<string, any> {
  const snapshot: Record<string, any> = {}
  for (const key of REQUEST_SNAPSHOT_FIELDS) {
    if (request[key] !== undefined && request[key] !== null) {
      snapshot[key] = request[key]
    }
  }
  snapshot.attachments = parseJsonArray<ProposalRequestAttachment>(request.attachments)
  snapshot.blueprint_links = parseJsonArray<ProposalRequestBlueprintLink>(request.blueprint_links)
  snapshot.intake_notes = parseRequestIntakeNotes(request.notes)
  snapshot.notes = snapshot.intake_notes
  snapshot.snapshot_at = new Date().toISOString()
  return snapshot
}

/** Same DB row becomes a proposal after conversion — never return to proposal-request URLs. */
export function resolveNavigationAfterProposalFromRequest(
  proposalId: string,
  convertedFromRequest: boolean,
  returnTo: string | null | undefined
): string {
  if (convertedFromRequest) {
    return `/proposals/view/${proposalId}`
  }
  if (returnTo) {
    const decoded = decodeURIComponent(returnTo)
    if (decoded.includes('/proposal-requests/')) {
      return `/proposals/view/${proposalId}`
    }
    return decoded
  }
  return `/proposals/view/${proposalId}`
}

export function proposalHasRequestOrigin(row: Record<string, any> | null | undefined): boolean {
  if (!row) return false
  if (row.proposal_request_data) return true
  if (row.converted_at) return true
  if (row.record_type === 'request') return true
  if (row.request_number) return true
  return false
}

export function resolveProposalRequestFromProposal(
  row: Record<string, any> | null | undefined
): ProposalRequestDisplayData | null {
  if (!row || !proposalHasRequestOrigin(row)) return null

  const snapshot =
    row.proposal_request_data && typeof row.proposal_request_data === 'object'
      ? row.proposal_request_data
      : null

  const attachments = parseJsonArray<ProposalRequestAttachment>(
    snapshot?.attachments ?? row.attachments
  )
  const blueprint_links = parseJsonArray<ProposalRequestBlueprintLink>(
    snapshot?.blueprint_links ?? row.blueprint_links
  )

  let intake_notes: ProposalRequestIntakeNote[] = []
  if (snapshot?.intake_notes) {
    intake_notes = parseRequestIntakeNotes(snapshot.intake_notes)
  } else if (snapshot?.notes) {
    intake_notes = parseRequestIntakeNotes(snapshot.notes)
  } else if (row.record_type === 'request') {
    intake_notes = parseRequestIntakeNotes(row.notes)
  }

  const requestStatus =
    snapshot?.status ??
    (row.converted_at || row.record_type === 'proposal' ? 'converted' : row.status)

  return {
    id: row.id || snapshot?.id,
    title: snapshot?.title ?? row.title,
    request_number: snapshot?.request_number ?? row.request_number,
    status: requestStatus,
    description: snapshot?.description ?? row.description,
    project_type: snapshot?.project_type ?? row.project_type,
    service_type: snapshot?.service_type ?? row.service_type,
    property_type: snapshot?.property_type ?? row.property_type,
    square_footage: snapshot?.square_footage ?? row.square_footage,
    estimated_budget: snapshot?.estimated_budget ?? row.estimated_budget,
    property_address: snapshot?.property_address ?? row.property_address,
    property_city: snapshot?.property_city ?? row.property_city,
    property_state: snapshot?.property_state ?? row.property_state,
    property_zip: snapshot?.property_zip ?? row.property_zip,
    preferred_start_date: snapshot?.preferred_start_date ?? row.preferred_start_date,
    preferred_completion_date:
      snapshot?.preferred_completion_date ?? row.preferred_completion_date,
    urgency_level: snapshot?.urgency_level ?? row.urgency_level,
    special_requirements: snapshot?.special_requirements ?? row.special_requirements,
    accessibility_needs: snapshot?.accessibility_needs ?? row.accessibility_needs,
    existing_contractor: snapshot?.existing_contractor ?? row.existing_contractor,
    contractor_details: snapshot?.contractor_details ?? row.contractor_details,
    internal_notes: snapshot?.internal_notes ?? row.internal_notes,
    review_notes: snapshot?.review_notes ?? row.review_notes,
    submitted_at: snapshot?.submitted_at ?? row.submitted_at,
    converted_at: row.converted_at,
    attachments,
    blueprint_links,
    intake_notes,
  }
}
